import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { dashboardApi } from "../../api/dashboardApi";
import { MachinesMapWidget } from "../../components/Machines/MachinesMapWidget";

const VENDORS = [
  { key: "IRINOX", label: "Irinox", icon: "bi-thermometer-sun", color: "#0ea5e9" },
  { key: "RATIONAL", label: "Rational", icon: "bi-cpu", color: "#6366f1" },
  { key: "FRIGOMAT", label: "Frigomat", icon: "bi-snow", color: "#38bdf8" },
  { key: "IFI_GALILEO", label: "IFI Galileo", icon: "bi-gear", color: "#8b5cf6" },
  { key: "ELETTROBAR", label: "Elettrobar", icon: "bi-cup-straw", color: "#f59e0b" },
];

const MAP_PROVIDERS = ["IRINOX", "RATIONAL", "FRIGOMAT", "IFI_GALILEO"];

function parseCoord(v: unknown): number | null {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return !Number.isNaN(n) ? n : null;
  }
  return null;
}

function fetchMachinesWithCoords(token: string | null) {
  return dashboardApi.getDashboard(token).then((dash: any) => {
    const items = dash?.machinesDashboard?.items ?? dash?.items ?? [];
    return items
      .filter((m: any) => MAP_PROVIDERS.includes(String(m?.provider || "").toUpperCase()))
      .map((m: any) => {
        const lat = parseCoord(m.latitude) ?? parseCoord(m.lat);
        const lng = parseCoord(m.longitude) ?? parseCoord(m.lng);
        if (lat == null || lng == null) return null;
        return { ...m, latitude: lat, longitude: lng };
      })
      .filter(Boolean);
  });
}

export default function MachinesHomePage() {
  const { token } = useAuth() as { token: string | null };
  const [allMachinesWithCoords, setAllMachinesWithCoords] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setRefreshing(true);
    fetchMachinesWithCoords(token)
      .then((filtered) => setAllMachinesWithCoords(filtered))
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  useEffect(() => {
    let mounted = true;
    fetchMachinesWithCoords(token).then((filtered) => {
      if (mounted) setAllMachinesWithCoords(filtered);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [token]);

  return (
    <div>
      <div className="machines-hero">
        <h1>Macchine</h1>
        <p className="lead mb-0">
          Seleziona un provider per accedere a dashboard, consumi e gestione allarmi
        </p>
      </div>

      {/* Mappa: Leaflet + OpenStreetMap. Coordinate da DB (MachineExternalRef.meta). Esegui setup:coords per popolarle. */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="small text-muted">
            <i className="bi bi-geo-alt me-1" />
            Leaflet · OpenStreetMap (non GPS live)
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={load}
            disabled={refreshing}
          >
            <i className={`bi ${refreshing ? "bi-arrow-repeat" : "bi-arrow-clockwise"} me-1`} />
            Aggiorna
          </button>
        </div>
        <MachinesMapWidget
          machines={allMachinesWithCoords}
          providerLabel="Irinox · Rational · Frigomat · IFI"
        />
        <p className="small text-muted mt-2 mb-0">
          <i className="bi bi-geo-alt me-1" />
          Tutte le macchine si trovano nella zona <strong>Via Zanardelli</strong> – <strong>Via Cesare Abba</strong>, Lecce.
        </p>
      </div>

      <div className="row g-4">
        {VENDORS.map((v) => (
          <div key={v.key} className="col-12 col-md-6 col-xl-4">
            <div className="vendor-card h-100">
              <div className="vendor-card-body">
                <div
                  className="vendor-card-icon text-white"
                  style={{ background: `linear-gradient(135deg, ${v.color} 0%, ${v.color}dd 100%)` }}
                >
                  <i className={`bi ${v.icon}`} />
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="vendor-card-title">{v.label}</span>
                  <span className="vendor-card-badge bg-light text-dark">{v.key}</span>
                </div>
                <p className="vendor-card-desc">
                  Dashboard vendor: stato, telemetria, consumi, allarmi.
                </p>
                <Link
                  to={`/machines/${v.key}`}
                  className="btn btn-primary vendor-card-btn"
                >
                  <i className="bi bi-arrow-right-circle me-2" />
                  Apri {v.label}
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
